import React, { useContext, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { FiltersContext } from '../../filters/context';
import AdvancedSearchBase from '../';
import { AdvancedSearchOrderContext } from './context';
import { PagingContext } from '../../contexts/paging-context';
import filterComponents from './filters';
import { getAdvancedSearchOrders } from './api';
import { useIsOnOrdersAdvancedSearchView } from '../hooks';
import { useShippySearchFilters } from './hooks/use-shippy-search-filters';
import { CURRENT_SEARCH_FILTERS_STORAGE_KEY, reverseMapFiltersToParams } from './constants/shippy-search-constants';
import { FIRST_PAGE, PAGE_SIZE_250 } from '../../services/pagination-service';

export const advancedSearch = 'advancedSearch';

const OrdersAdvancedSearchContainer = ({ onSubmit, switchToAdvancedSearchView, orderBy, orderByDirection }) => {
  const isOnAdvancedSearch = useIsOnOrdersAdvancedSearchView();
  const shippySearchFilters = useShippySearchFilters();
  const { filters, resetFilters, setFilters } = useContext(AdvancedSearchOrderContext);
  const { page, pageSize } = useContext(PagingContext);
  const {
    buildApiFilters,
    filters: contextFilters,
    replaceFiltersForSource,
    replaceFilters: replaceAllContextFilters,
  } = useContext(FiltersContext);

  const makeApiCall = useCallback(
    (pageNumber = FIRST_PAGE, pageCount = PAGE_SIZE_250) => {
      const apiFilters = buildApiFilters();
      return getAdvancedSearchOrders(apiFilters, pageNumber, pageCount, orderBy, orderByDirection);
    },
    [orderBy, orderByDirection, buildApiFilters],
  );

  const updateFiltersContext = useCallback(
    (shouldReplaceAllFilters = false) => {
      const filterMap = filters.reduce((acc, filter) => {
        return [...acc, { ...filter, source: advancedSearch }];
      }, []);
      if (shouldReplaceAllFilters) {
        replaceAllContextFilters(filterMap);
        return;
      }
      replaceFiltersForSource(advancedSearch, filterMap);
    },
    [filters, replaceFiltersForSource, replaceAllContextFilters],
  );

  const onSubmitCallback = useCallback(() => {
    updateFiltersContext(!isOnAdvancedSearch);
    switchToAdvancedSearchView();
  }, [switchToAdvancedSearchView, updateFiltersContext, isOnAdvancedSearch]);

  const didMount = useRef(false);

  useEffect(
    () => {
      if (isOnAdvancedSearch && !didMount.current) {
        updateFiltersContext(true);
      }
      if (isOnAdvancedSearch && didMount.current) {
        onSubmit(makeApiCall, page, pageSize);
      }
      didMount.current = true;
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, orderBy, orderByDirection, contextFilters],
  );

  useEffect(() => {
    if (!shippySearchFilters) {
      return;
    }

    // Update form state (for display)
    setFilters(shippySearchFilters);

    const filterMap = shippySearchFilters.map((filter) => ({ ...filter, source: advancedSearch }));
    replaceAllContextFilters(filterMap);
    switchToAdvancedSearchView({ clearRouteState: true });
  }, [replaceAllContextFilters, setFilters, shippySearchFilters, switchToAdvancedSearchView]);

  // Sync active filters to sessionStorage so Shippy can read them for follow-up context
  useEffect(() => {
    try {
      const mapped = reverseMapFiltersToParams(contextFilters);
      sessionStorage.setItem(CURRENT_SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(mapped));
    } catch {
      // Ignore sessionStorage errors (e.g., quota exceeded)
    }
  }, [contextFilters]);

  // Clean up sessionStorage and filter context on unmount
  useEffect(
    () => {
      return () => {
        sessionStorage.removeItem(CURRENT_SEARCH_FILTERS_STORAGE_KEY);
        replaceFiltersForSource(advancedSearch);
      };
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return <AdvancedSearchBase filterComponents={filterComponents} onReset={resetFilters} onSubmit={onSubmitCallback} />;
};

OrdersAdvancedSearchContainer.propTypes = {
  switchToAdvancedSearchView: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  orderBy: PropTypes.string.isRequired,
  orderByDirection: PropTypes.string.isRequired,
};

export default OrdersAdvancedSearchContainer;
